import { type FC, type PropsWithChildren, useId } from 'react';

import { RecipeVariants } from '@vanilla-extract/recipes';

import { svgIllustrationRecipe } from './IllustrationWrapper.css';
import { theme } from '../theme.css';
import tokens from '../themes/eneco/tokens';
import { getClassNames } from '../util/css';

const sparkyClassName = 'illustration-sparky';

export const Illustration: FC<PropsWithChildren<IllustrationProps>> = ({ children, color = 'iconPrimary', size }) => {
  const gradientId = useId();

  // For token-based colors, use the recipe's color variant.
  // For special values (currentColor, brandGradient), apply fill via inline style.
  const isTokenColor = color !== 'currentColor' && color !== 'brandGradient';

  let fillStyle: string | undefined;
  switch (color) {
    case 'currentColor':
      fillStyle = 'currentColor';
      break;

    case 'brandGradient':
      fillStyle = `url(#${gradientId})`;
      break;

    default:
      fillStyle = undefined;
  }

  const illustrationClassNames = getClassNames(
    svgIllustrationRecipe({
      size,
      color: isTokenColor ? color : undefined,
    }),
    sparkyClassName,
  );

  return (
    <svg
      className={illustrationClassNames}
      aria-hidden="true"
      viewBox="0 0 80 80"
      style={fillStyle ? { fill: fillStyle } : undefined}>
      {children}
      {color === 'brandGradient' && (
        <defs>
          <linearGradient id={gradientId}>
            <stop offset="0%" style={{ stopColor: theme.colors.brandGradientStart }} />
            <stop offset="100%" style={{ stopColor: theme.colors.brandGradientEnd }} />
          </linearGradient>
        </defs>
      )}
    </svg>
  );
};

type IllustrationVariants = RecipeVariants<typeof svgIllustrationRecipe>;
type IconColors = keyof typeof tokens.iconColors | keyof typeof tokens.feedbackColors | 'brandGradient';

export interface IllustrationProps extends Omit<NonNullable<IllustrationVariants>, 'color'> {
  /** The color of the Icon, by default it will inherit the `currentColor`.*/
  color?: IconColors;
}

Illustration.toString = () => `.${sparkyClassName}`;
